/**
 * Add Layout Dialog - New Implementation
 *
 * Add asset layout from template library.
 * Simplified without language dictionary - uses hardcoded templates.
 */
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Modal, Button, Select } from "@gpstrackit/gtc-react-controls";
import { v4 as uuidv4 } from "uuid";

// Hardcoded asset layout templates
const LAYOUT_TEMPLATES = [
	{
		id: "tractor-template",
		name: "Tractor",
		template: {
			layoutId: uuidv4(),
			layoutName: "Tractor",
			assetType: ["tractor"],
			assetViewId: null,
			assetViewGrid: [7, 7],
			components: [
				{ component: "Tires", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Brakes", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Lights", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Engine", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Steering", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Mirrors", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Windshield", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Horn", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
			],
			configZones: [
				{
					tag: "Front",
					tagNumber: 1,
					tagType: "Zone",
					tagComponents: [0, 2, 5, 6, 7],
					inspectionTypes: [],
					assetViewLocation: [3, 0],
				},
				{
					tag: "Left Side",
					tagNumber: 2,
					tagType: "Zone",
					tagComponents: [0, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [0, 3],
				},
				{
					tag: "Right Side",
					tagNumber: 3,
					tagType: "Zone",
					tagComponents: [0, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [6, 3],
				},
				{
					tag: "Rear",
					tagNumber: 4,
					tagType: "Zone",
					tagComponents: [0, 1, 2],
					inspectionTypes: [],
					assetViewLocation: [3, 6],
				},
				{
					tag: "Engine Compartment",
					tagNumber: 5,
					tagType: "Zone",
					tagComponents: [3],
					inspectionTypes: [],
					assetViewLocation: [3, 2],
				},
				{
					tag: "Cab",
					tagNumber: 6,
					tagType: "Zone",
					tagComponents: [4, 7],
					inspectionTypes: [],
					assetViewLocation: [3, 3],
				},
			],
		},
	},
	{
		id: "trailer-template",
		name: "Trailer",
		template: {
			layoutId: uuidv4(),
			layoutName: "Trailer",
			assetType: ["trailer"],
			assetViewId: null,
			assetViewGrid: [7, 7],
			components: [
				{ component: "Tires", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Brakes", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Lights", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Coupling", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Floor", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Doors", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
			],
			configZones: [
				{
					tag: "Front",
					tagNumber: 1,
					tagType: "Zone",
					tagComponents: [3],
					inspectionTypes: [],
					assetViewLocation: [3, 0],
				},
				{
					tag: "Left Side",
					tagNumber: 2,
					tagType: "Zone",
					tagComponents: [0, 2],
					inspectionTypes: [],
					assetViewLocation: [0, 3],
				},
				{
					tag: "Right Side",
					tagNumber: 3,
					tagType: "Zone",
					tagComponents: [0, 2],
					inspectionTypes: [],
					assetViewLocation: [6, 3],
				},
				{
					tag: "Rear",
					tagNumber: 4,
					tagType: "Zone",
					tagComponents: [1, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [3, 6],
				},
				{
					tag: "Interior",
					tagNumber: 5,
					tagType: "Zone",
					tagComponents: [4],
					inspectionTypes: [],
					assetViewLocation: [3, 3],
				},
			],
		},
	},
	{
		id: "bus-template",
		name: "Bus",
		template: {
			layoutId: uuidv4(),
			layoutName: "Bus",
			assetType: ["bus"],
			assetViewId: null,
			assetViewGrid: [7, 7],
			components: [
				{ component: "Tires", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Brakes", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Lights", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Engine", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Steering", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Mirrors", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Emergency Exit", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Seats", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Wheelchair Lift", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
			],
			configZones: [
				{
					tag: "Front",
					tagNumber: 1,
					tagType: "Zone",
					tagComponents: [0, 2, 5, 4],
					inspectionTypes: [],
					assetViewLocation: [3, 0],
				},
				{
					tag: "Left Side",
					tagNumber: 2,
					tagType: "Zone",
					tagComponents: [0, 2, 6],
					inspectionTypes: [],
					assetViewLocation: [0, 3],
				},
				{
					tag: "Right Side",
					tagNumber: 3,
					tagType: "Zone",
					tagComponents: [0, 2, 8],
					inspectionTypes: [],
					assetViewLocation: [6, 3],
				},
				{
					tag: "Rear",
					tagNumber: 4,
					tagType: "Zone",
					tagComponents: [1, 2, 3, 6],
					inspectionTypes: [],
					assetViewLocation: [3, 6],
				},
				{
					tag: "Interior",
					tagNumber: 5,
					tagType: "Zone",
					tagComponents: [6, 7],
					inspectionTypes: [],
					assetViewLocation: [3, 3],
				},
			],
		},
	},
	{
		id: "van-template",
		name: "Van",
		template: {
			layoutId: uuidv4(),
			layoutName: "Van",
			assetType: ["van"],
			assetViewId: null,
			assetViewGrid: [7, 7],
			components: [
				{ component: "Tires", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Brakes", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Lights", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Engine", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Mirrors", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Doors", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
			],
			configZones: [
				{
					tag: "Front",
					tagNumber: 1,
					tagType: "Zone",
					tagComponents: [0, 2, 4],
					inspectionTypes: [],
					assetViewLocation: [3, 0],
				},
				{
					tag: "Left Side",
					tagNumber: 2,
					tagType: "Zone",
					tagComponents: [0, 2, 4, 5],
					inspectionTypes: [],
					assetViewLocation: [0, 3],
				},
				{
					tag: "Right Side",
					tagNumber: 3,
					tagType: "Zone",
					tagComponents: [0, 2, 4, 5],
					inspectionTypes: [],
					assetViewLocation: [6, 3],
				},
				{
					tag: "Rear",
					tagNumber: 4,
					tagType: "Zone",
					tagComponents: [1, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [3, 6],
				},
				{
					tag: "Engine",
					tagNumber: 5,
					tagType: "Zone",
					tagComponents: [3],
					inspectionTypes: [],
					assetViewLocation: [3, 2],
				},
			],
		},
	},
	{
		id: "straight-truck-template",
		name: "Straight Truck",
		template: {
			layoutId: uuidv4(),
			layoutName: "Straight Truck",
			assetType: ["truck"],
			assetViewId: null,
			assetViewGrid: [7, 7],
			components: [
				{ component: "Tires", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Brakes", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Lights", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Engine", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Steering", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Mirrors", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Cargo Door", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
				{ component: "Liftgate", maxSeverity: 255, minSeverity: 0, conditions: ["Good", "Needs Repair", "Missing", "Damaged"] },
			],
			configZones: [
				{
					tag: "Front",
					tagNumber: 1,
					tagType: "Zone",
					tagComponents: [0, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [3, 0],
				},
				{
					tag: "Left Side",
					tagNumber: 2,
					tagType: "Zone",
					tagComponents: [0, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [0, 3],
				},
				{
					tag: "Right Side",
					tagNumber: 3,
					tagType: "Zone",
					tagComponents: [0, 2, 5],
					inspectionTypes: [],
					assetViewLocation: [6, 3],
				},
				{
					tag: "Rear",
					tagNumber: 4,
					tagType: "Zone",
					tagComponents: [1, 2, 6, 7],
					inspectionTypes: [],
					assetViewLocation: [3, 6],
				},
				{
					tag: "Cab",
					tagNumber: 5,
					tagType: "Zone",
					tagComponents: [3, 4],
					inspectionTypes: [],
					assetViewLocation: [3, 2],
				},
				{
					tag: "Cargo Area",
					tagNumber: 6,
					tagType: "Zone",
					tagComponents: [],
					inspectionTypes: [],
					assetViewLocation: [3, 4],
				},
			],
		},
	},
];

/**
 * Dialog for adding an asset layout from the template library
 */
const AddLayoutDialog = ({ open, onClose, onAdd }) => {
	const { messages } = useIntl();
	const [selectedTemplate, setSelectedTemplate] = useState("");

	const handleAdd = () => {
		const template = LAYOUT_TEMPLATES.find((t) => t.id === selectedTemplate);
		if (template) {
			// Generate new UUIDs for the layout
			const newLayout = {
				...template.template,
				layoutId: uuidv4(),
			};
			onAdd(newLayout);
			handleClose();
		}
	};

	const handleClose = () => {
		setSelectedTemplate("");
		onClose();
	};

	return (
		<Modal
			title={messages.addAssetLayoutFromTemplateLibrary || "Add Asset Layout from Template Library"}
			open={open}
			handleClose={handleClose}
			size="small"
			footer={
				<div>
					<Button
						variant="secondary"
						onClick={handleClose}
						style={{ marginRight: "5px" }}
					>
						{messages.cancel || "Cancel"}
					</Button>
					<Button
						variant="primary"
						onClick={handleAdd}
						isDisabled={!selectedTemplate}
					>
						{messages.addLayout || "Add Layout"}
					</Button>
				</div>
			}
		>
			<div style={{ padding: "15px" }}>
				<p style={{ marginBottom: "16px" }}>
					{messages.selectAssetLayoutTemplateDescription || "Select an asset layout template to add to your configuration."}
				</p>

				<Select
					id="template-select"
					placeholder={messages.assetLayoutTemplate || "Asset Layout Template"}
					value={LAYOUT_TEMPLATES.find((t) => t.id === selectedTemplate) || null}
					onChange={(selected) => setSelectedTemplate(selected?.id || "")}
					options={LAYOUT_TEMPLATES}
					textField="name"
					valueField="id"
				/>
			</div>
		</Modal>
	);
};

export default AddLayoutDialog;
